import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * Session metadata — tempo, musical key, dry mode, metronome. All four are
 * UI-side preferences that persist across reloads and (for tempo + dry/met)
 * map directly onto AudioEngine setters.
 *
 * Returns the values + setters + ready-to-wire toggle handlers (so the
 * caller doesn't have to re-implement the toast / engine-sync wiring).
 */
export default function useSessionMetadata(engine) {
  const [tempo, setTempo] = useState(() => {
    const stored = typeof window !== "undefined" ? Number(localStorage.getItem("elpm-tempo")) : 0;
    return Number.isFinite(stored) && stored >= 60 && stored <= 240 ? stored : 120;
  });
  const [musicalKey, setMusicalKey] = useState(() => {
    return (typeof window !== "undefined" ? localStorage.getItem("elpm-key") : null) || "C Major";
  });
  // iter-79: default to Dry mode so brand-new sessions sound clean (no
  // reverb/delay wet tail). Persisted across reloads via localStorage.
  const [dryMode, setDryMode] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("elpm-dry-mode") : null;
    if (stored === "0") return false;     // user explicitly went Wet
    return true;                          // default (and stored === "1") = Dry
  });
  const [metronomeOn, setMetronomeOn] = useState(false);

  // Push dryMode into the engine on mount AND whenever it changes so the
  // wet returns stay in sync (engine constructor sets dryMode=true but the
  // setter is what actually zeroes the live reverbWet/delayWet gains).
  useEffect(() => {
    engine.setDryMode(dryMode);
  }, [engine, dryMode]);

  // Push tempo into the engine on mount AND every time it changes. Tempo
  // drives the delay-FX tempo-sync on every channel, so the engine needs
  // this value even before the user touches the Tempo pill.
  //
  // This effect is the SINGLE source of engine sync — `handleTempoChange`
  // intentionally does NOT also call engine.setTempo so we don't fire it
  // twice on every pill commit (flagged in iter-30 code review).
  useEffect(() => {
    engine.setTempo(tempo);
  }, [engine, tempo]);

  const handleTempoChange = useCallback((next) => {
    setTempo(next);
    try { localStorage.setItem("elpm-tempo", String(next)); } catch (e) { void e; }
    // Engine sync happens via the useEffect above when `tempo` state lands.
  }, []);

  const handleKeyChange = useCallback((next) => {
    setMusicalKey(next);
    try { localStorage.setItem("elpm-key", next); } catch (e) { void e; }
  }, []);

  const handleToggleDryMode = useCallback(() => {
    setDryMode((prev) => {
      const next = !prev;
      engine.setDryMode(next);
      try { localStorage.setItem("elpm-dry-mode", next ? "1" : "0"); } catch (e) { void e; }
      toast.message(next ? "Dry mode ON — reverb + delay bypassed" : "Wet mode — FX restored");
      return next;
    });
  }, [engine]);

  const handleToggleMetronome = useCallback(() => {
    const next = engine.toggleMetronome();
    setMetronomeOn(next);
    toast.message(next ? `Metronome ON · ${tempo} BPM` : "Metronome OFF");
  }, [engine, tempo]);

  return {
    tempo, setTempo,
    musicalKey, setMusicalKey,
    dryMode, setDryMode,
    metronomeOn, setMetronomeOn,
    handleTempoChange,
    handleKeyChange,
    handleToggleDryMode,
    handleToggleMetronome,
  };
}
